import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Button from '../../Components/Button/Button';
import { getApi, postApi } from '../../Services/apiService';
import { toast } from 'react-toastify';
import { TRIAL_DAYS, readSubscriptionResponse, formatApiDate } from '../../Utils/subscriptionTrial';
import { clearSession } from '../../Utils/postLoginNavigation';

// Get Stripe publishable key from environment
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

// Load Stripe with error handling - prevents app crash when Stripe.js fails to load (network, ad blocker, etc.)
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey).catch((err) => {
      console.warn('Stripe failed to load. Payment may be unavailable.', err?.message || err);
      return null;
    })
  : null;

// iOS zooms into inputs (Stripe's iframes included) under 16px, so phones get 16px.
const isSmallScreen = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 768px)').matches;

const elementOptions = {
  style: {
    base: {
      fontSize: isSmallScreen ? '16px' : '14px',
      color: '#2c2c2c',
      fontFamily:
        "'Be Vietnam Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      '::placeholder': { 
        color: '#9aa0a6',
        fontFamily: "'Be Vietnam Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      },
    },
    invalid: { color: '#dc3545' },
  },
};

// Prices come back as plain numbers (11.99, 29). Keep whole amounts clean: $29, not $29.00
const formatPrice = (price) => {
  const value = Number(price);
  if (!Number.isFinite(value)) return '$0';
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
};

const formatInterval = (interval) => {
  const value = String(interval || 'month').toLowerCase();
  if (value === 'monthly') return 'month';
  if (value === 'yearly' || value === 'annually') return 'year';
  if (value === 'weekly') return 'week';
  return value;
};

// The plans API only returns name/price/interval, so the tier copy lives here,
// keyed by the plan name coming back from /subscriptions/plans.
const PLAN_TIER_DETAILS = {
  basic: { range: '1 - 5 properties', tagline: 'For landlords getting started' },
  standard: { range: '6 - 15 properties', tagline: 'For growing portfolios' },
  enterprise: { range: '16+ properties', tagline: 'For large portfolios' },
};

// Everything is included in every tier - the tiers differ by property count.
const SHARED_PLAN_FEATURES = [
  'Unlimited tenant screenings',
  'Automated voice interviews & document verification',
  'Automated reference checks',
];

// "Platform fee" is not a tier the landlord chooses, so it is kept out of the picker.
const isSelectableTier = (plan) => !/platform\s*fee/i.test(String(plan?.name || ''));

const getTierDetails = (plan) => PLAN_TIER_DETAILS[String(plan?.name || '').trim().toLowerCase()] || null;

// The trial is granted by the backend, not requested by the picker. When the
// plans API happens to advertise a trial we label the button accordingly,
// otherwise the button stays "Pay Now" and the trial (if the account is
// eligible) shows up in the /subscriptions/create response instead.
const getPlanTrialDays = (plan) => {
  const raw = plan?.trial_days ?? plan?.trial_period_days ?? plan?.trialDays ?? plan?.trialPeriodDays;
  const days = Number(raw);
  if (Number.isFinite(days) && days > 0) return days;

  const eligible = plan?.trial_eligible ?? plan?.is_trial_eligible ?? plan?.isTrialEligible;
  const isEligible = eligible === true || eligible === 1 || eligible === '1' || eligible === 'true';
  return isEligible ? TRIAL_DAYS : 0;
};

// Shared Stripe card inputs - the trial card-setup form and the payment form
// collect exactly the same fields.
function CardFields() {
  const elements = useElements();

  // Card number complete -> jump to expiry, expiry complete -> jump to CVC.
  // Browser autofill fills the whole card in one shot, so the field jumps from
  // empty straight to complete in a single change event. Moving focus in the
  // middle of that cancels the fill and leaves the card input stuck - it looks
  // disabled and stops accepting typing. Typing produces a change per
  // keystroke, so only auto-advance once we have seen more than one change.
  const numberEdits = useRef(0);
  const expiryEdits = useRef(0);

  const handleCardNumberChange = (event) => {
    numberEdits.current = event.empty ? 0 : numberEdits.current + 1;
    if (!event.complete || numberEdits.current < 2 || !elements) return;
    const expiryElement = elements.getElement(CardExpiryElement);
    if (expiryElement) {
      // Small delay to ensure smooth transition
      setTimeout(() => {
        expiryElement.focus();
      }, 50);
    }
  };

  const handleExpiryChange = (event) => {
    expiryEdits.current = event.empty ? 0 : expiryEdits.current + 1;
    if (!event.complete || expiryEdits.current < 2 || !elements) return;
    const cvcElement = elements.getElement(CardCvcElement);
    if (cvcElement) {
      // Small delay to ensure smooth transition
      setTimeout(() => {
        cvcElement.focus();
      }, 50);
    }
  };

  return (
    <>
      <div className="payment-field">
        <label className="payment-label">Card Number</label>
        <div className="payment-stripe-input payment-stripe-input-with-icon" data-autocomplete="off">
          <svg className="payment-card-icon" width="20" height="16" viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.5" y="3.5" width="23" height="12" rx="1.5" stroke="#9aa0a6" strokeWidth="1"/>
            <rect x="2" y="6" width="20" height="2" rx="1" fill="#9aa0a6"/>
            <rect x="2" y="10" width="8" height="2" rx="1" fill="#9aa0a6"/>
          </svg>
          <CardNumberElement 
            options={{ ...elementOptions, disableLink: true }} 
            onChange={handleCardNumberChange}
          />
        </div>
      </div>

      <div className="payment-row">
        <div className="payment-field">
          <label className="payment-label">Expiry</label>
          <div className="payment-stripe-input" data-autocomplete="off">
            <CardExpiryElement 
              options={{ ...elementOptions, disableLink: true }} 
              onChange={handleExpiryChange}
            />
          </div>
        </div>
        <div className="payment-field">
          <label className="payment-label">CVC</label>
          <div className="payment-stripe-input" data-autocomplete="off">
            <CardCvcElement options={{ ...elementOptions, disableLink: true }} />
          </div>
        </div>
      </div>
    </>
  );
}

function PlanSelector({ plans, onChoosePlan, pendingPlanId }) {
  return (
    <div className="plan-select-section">
      <h2 className="payment-form-title">Choose your plan</h2>
      <p className="payment-form-subtitle">
        Pick the plan that fits your portfolio - new landlords start with a {TRIAL_DAYS}-day free trial
      </p>

      <div className="plan-card-grid">
        {plans.map((plan) => {
          const interval = formatInterval(plan.interval);
          const details = getTierDetails(plan);
          const isPending = pendingPlanId === plan.id;
          const trialDays = getPlanTrialDays(plan);

          return (
            <div key={plan.id} className={`plan-card${isPending ? ' pending' : ''}`}>
              {plan.is_current_plan && <span className="plan-card-badge">Current plan</span>}

              <div className="plan-card-name">{plan.name}</div>

              <div className="plan-card-pricing">
                <span className="plan-card-price">{formatPrice(plan.price)}</span>
                <span className="plan-card-interval">/{interval}</span>
              </div>

              <div className="plan-card-range">{details?.range || `${formatPrice(plan.price)} per ${interval}`}</div>
              {details?.tagline && <div className="plan-card-tagline">{details.tagline}</div>}

              <Button
                type="button"
                variant="success"
                className="plan-card-button"
                disabled={Boolean(pendingPlanId)}
                onClick={() => onChoosePlan(plan)}
                aria-label={
                  trialDays
                    ? `Start the ${trialDays}-day free trial on the ${plan.name} plan`
                    : `Pay now for the ${plan.name} plan`
                }
              >
                {isPending ? 'Please wait...' : trialDays ? 'Start Free Trial' : 'Pay Now'}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="plan-includes">
        <div className="plan-includes-title">All plans include</div>
        <ul className="plan-includes-list">
          <li className="plan-includes-highlight">
            {TRIAL_DAYS}-day free trial for new landlords - cancel any time before it ends
          </li>
          {SHARED_PLAN_FEATURES.map((feature) => (
            <li key={feature}>{feature}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Trial has already started server-side; this only saves a card (SetupIntent) so
// the subscription can bill itself when the trial runs out. Card Elements use
// confirmCardSetup - the Payment Element equivalent of stripe.confirmSetup().
function TrialCardSetupForm({
  amountText = '$0',
  intervalLabel = 'month',
  planLabel = 'Full access to the platform for landlord',
  trialEnd,
  clientSecret,
  onSetupSuccess,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);

  const trialEndText = trialEnd ? formatApiDate(trialEnd) : '';

  const handleSaveCard = async (e) => {
    e.preventDefault();

    if (!clientSecret) {
      toast.error('Card setup is not ready yet. Please wait a moment and try again.');
      return;
    }
    if (!stripe || !elements) return;

    setIsLoading(true);
    try {
      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) {
        toast.error('Card element not ready. Please try again.');
        return;
      }

      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardNumber,
        },
      });

      if (error) {
        toast.error(error.message || 'We could not save your card. Please try again.');
        return;
      }

      if (setupIntent && setupIntent.status === 'succeeded') {
        toast.success('Card saved - your free trial is all set');
        onSetupSuccess?.();
      } else {
        toast.info(`Card setup status: ${setupIntent?.status || 'unknown'}`);
      }
    } catch (err) {
      toast.error(err?.message || 'We could not save your card. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="payment-form-section">
      <h2 className="payment-form-title">Your {TRIAL_DAYS}-day free trial has started</h2>
      <p className="payment-form-subtitle">Add a card so your plan continues when the trial ends</p>

      <div className="payment-summary-card">
        <div className="trial-banner">
          <span className="trial-banner-badge">Free trial</span>
          <span className="trial-banner-text">
            {trialEndText
              ? `No charge today. Your trial runs until ${trialEndText}.`
              : `No charge today. Your trial runs for ${TRIAL_DAYS} days.`}
          </span>
        </div>

        <div className="payment-summary-row">
          <div className="payment-summary-label">{planLabel}</div>
          <div className="payment-summary-price">{amountText}/{intervalLabel}</div>
        </div>
        <div className="payment-summary-divider" />
        <div className="payment-summary-row total">
          <div className="payment-summary-label">Due today</div>
          <div className="payment-summary-total">$0</div>
        </div>
        <div className="payment-summary-footnote">
          {trialEndText
            ? `Then ${amountText}/${intervalLabel} from ${trialEndText}`
            : `Then ${amountText}/${intervalLabel} once the trial ends`}
        </div>
      </div>

      <form onSubmit={handleSaveCard} className="payment-card-form" noValidate autoComplete="off" data-form-type="other">
        <CardFields />

        <Button type="submit" variant="success" className="payment-submit-button" disabled={isLoading || !clientSecret}>
          {isLoading ? 'Saving...' : 'Save card & start trial'}
        </Button>
      </form>
    </div>
  );
}

function PaymentForm({
  amountText = '$0',
  intervalLabel = 'month',
  planLabel = 'Full access to the platform for landlord',
  clientSecret,
  onPaymentSuccess,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);

  const handlePay = async (e) => {
    e.preventDefault();

    if (!clientSecret) {
      toast.error('Payment is not ready yet. Please wait a moment and try again.');
      return;
    }
    if (!stripe || !elements) return;

    setIsLoading(true);
    try {
      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) {
        toast.error('Card element not ready. Please try again.');
        return;
      }

      // Confirm payment intent with Stripe - this calls /payment_intents/{id}/confirm
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardNumber,
        },
      });

      if (error) {
        toast.error(error.message || 'Payment failed. Please try again.');
        return;
      }

      // Payment confirmed successfully - proceed to success screen
      if (paymentIntent && paymentIntent.status === 'succeeded') {
        toast.success('Payment successful');
        onPaymentSuccess?.();
      } else {
        toast.info(`Payment status: ${paymentIntent?.status || 'unknown'}`);
      }
    } catch (err) {
      toast.error(err?.message || 'Payment failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="payment-form-section">
      <h2 className="payment-form-title">Complete Payment</h2>
      <p className="payment-form-subtitle">Secure payment for Landlord</p>

      <div className="payment-summary-card">
        <div className="payment-summary-row">
          <div className="payment-summary-label">{planLabel}</div>
          <div className="payment-summary-price">{amountText}/{intervalLabel}</div>
        </div>
        <div className="payment-summary-divider" />
        <div className="payment-summary-row total">
          <div className="payment-summary-label">Total</div>
          <div className="payment-summary-total">{amountText}</div>
        </div>
        <div className="payment-summary-footnote">Valid for 1 {intervalLabel}</div>
      </div>

      <form onSubmit={handlePay} className="payment-card-form" noValidate autoComplete="off" data-form-type="other">
        <CardFields />

        <Button type="submit" variant="success" className="payment-submit-button" disabled={isLoading || !clientSecret}>
          {isLoading ? 'Processing...' : `Pay ${amountText}`}
        </Button>
      </form>
    </div>
  );
}

const CompletePayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Read once: onboarding wipes localStorage on the success screen, and this
  // must not flip to "not a landlord" underneath the screen that just rendered.
  const roleRef = useRef(location.state?.role || localStorage.getItem('role'));
  const role = roleRef.current;
  // 'paid' | 'trial' | 'active' - decides the success copy.
  const [successMode, setSuccessMode] = useState('');
  const [isPlansLoading, setIsPlansLoading] = useState(true);
  const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);
  const [initError, setInitError] = useState('');
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [pendingPlanId, setPendingPlanId] = useState('');
  const [step, setStep] = useState('select'); // 'select' | 'pay' | 'trial-setup'
  const [clientSecret, setClientSecret] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [trialEnd, setTrialEnd] = useState(null);

  // Cache the created subscription per plan so switching back and forth
  // doesn't create a new subscription every time.
  const subscriptionCacheRef = useRef({});

  // Only for landlords; tenants should not land here.
  const isLandlord = role === 'landlord' || role === '1' || role === 1;

  const selectedPlan = useMemo(
    () => plans.find((plan) => plan.id === selectedPlanId) || null,
    [plans, selectedPlanId]
  );

  // Mark that user is on payment page so logo click doesn't redirect to dashboard
  useEffect(() => {
    if (isLandlord) {
      localStorage.setItem('onPaymentPage', 'true');
    }
    return () => {};
  }, [isLandlord]);

  useEffect(() => {
    if (isLandlord) return;
    // No session left - e.g. a refresh after onboarding finished and cleared
    // storage - means the only way forward is signing in. Anyone else who hits
    // this URL directly still gets the role picker.
    navigate(localStorage.getItem('token') ? '/choose-role' : '/login', { replace: true });
  }, [isLandlord, navigate]);

  useEffect(() => {
    let cancelled = false;

    const fetchPlans = async () => {
      setIsPlansLoading(true);
      setInitError('');
      try {
        const plansRes = await getApi('/subscriptions/plans');
        const list =
          plansRes?.data?.plans ||
          plansRes?.data?.data?.plans ||
          plansRes?.plans ||
          [];

        const availablePlans = list.filter((plan) => plan?.id && isSelectableTier(plan));

        if (!availablePlans.length) {
          throw new Error('No subscription plans are available right now');
        }

        if (cancelled) return;

        setPlans(availablePlans);
        // Preselect the current plan if there is one, otherwise the cheapest plan.
        const preselected =
          availablePlans.find((plan) => plan.is_current_plan) ||
          [...availablePlans].sort((a, b) => Number(a.price || 0) - Number(b.price || 0))[0];
        setSelectedPlanId(preselected.id);
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.data?.message || e?.message || 'Failed to load subscription plans';
          setInitError(msg);
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setIsPlansLoading(false);
      }
    };

    if (isLandlord) fetchPlans();
    return () => {
      cancelled = true;
    };
  }, [isLandlord]);

  // Onboarding ends here. The session that carried the landlord from signup OTP
  // through payment is thrown away, so the dashboard can only be reached by
  // signing in and clearing the login OTP - a refresh on the success screen
  // lands on /login instead of the dashboard.
  const finishSubscription = () => {
    clearSession();
  };

  // Routes the /subscriptions/create outcome to the right screen:
  // trialing -> save a card (or straight to success), paid -> card payment,
  // anything else -> the subscription is already active.
  const applySubscriptionOutcome = (outcome) => {
    setClientSecret(outcome.clientSecret);
    setSubscriptionId(outcome.subscriptionId);
    setTrialEnd(outcome.trialEnd);

    if (outcome.nextAction === 'setup') {
      setStep('trial-setup');
      return;
    }

    if (outcome.nextAction === 'pay') {
      setStep('pay');
      return;
    }

    // Nothing left to confirm with Stripe.js.
    finishSubscription();
    setSuccessMode(outcome.trialing ? 'trial' : 'active');
  };

  const handleChoosePlan = async (plan) => {
    if (!plan?.id || isCreatingSubscription) return;

    setSelectedPlanId(plan.id);

    const cached = subscriptionCacheRef.current[plan.id];
    if (cached) {
      applySubscriptionOutcome(cached);
      return;
    }

    setPendingPlanId(plan.id);
    setIsCreatingSubscription(true);
    setInitError('');
    try {
      // No trial flag is sent - the backend decides eligibility on its own.
      const createRes = await postApi('/subscriptions/create', {
        plan_id: plan.id,
      });

      const outcome = readSubscriptionResponse(createRes);

      // A client secret is only required when Stripe.js still has something to
      // confirm; a trial with the card already on file returns none.
      if (outcome.nextAction !== 'done' && !outcome.clientSecret) {
        throw new Error(createRes?.message || 'Missing client_secret from /subscriptions/create');
      }

      subscriptionCacheRef.current[plan.id] = outcome;
      applySubscriptionOutcome(outcome);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to start the subscription';
      setInitError(msg);
      toast.error(msg);
    } finally {
      setIsCreatingSubscription(false);
      setPendingPlanId('');
    }
  };

  const amountText = useMemo(() => formatPrice(selectedPlan?.price), [selectedPlan?.price]);
  const intervalLabel = useMemo(() => formatInterval(selectedPlan?.interval), [selectedPlan?.interval]);
  const planLabel = useMemo(() => {
    if (!selectedPlan?.name) return 'Full access to the platform for landlord';
    const details = getTierDetails(selectedPlan);
    return details?.range ? `${selectedPlan.name} plan - ${details.range}` : `${selectedPlan.name} plan`;
  }, [selectedPlan]);

  if (!isLandlord) return null;

  // Show success screen once the subscription is settled (paid, trialing or already active)
  if (successMode) {
    const trialEndText = trialEnd ? formatApiDate(trialEnd) : '';
    const isTrialSuccess = successMode === 'trial';

    return (
      <div className="verification-success-page">
        <div className="verification-success-container">
          <div className="verification-success-card">
            <div className="verification-success-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white"/>
              </svg>
            </div>
            <h1 className="verification-success-title">
              {isTrialSuccess ? `Your ${TRIAL_DAYS}-day free trial has started` : 'Welcome to Clear Rent'}
            </h1>
            <p className="verification-success-message">
              {isTrialSuccess
                ? trialEndText
                  ? `You have full access until ${trialEndText}.`
                  : `You have full access for the next ${TRIAL_DAYS} days.`
                : "You've successfully created and verified an account."}
            </p>
            <Button 
              variant="success" 
              className="verification-success-button"
              onClick={() => {
                clearSession();
                navigate('/login', { replace: true });
              }}
            >
              Login to access
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    if (!stripePromise) {
      return (
        <div className="payment-error-message">
          <h2 className="payment-form-title">Complete Payment</h2>
          <p className="payment-form-subtitle">Secure payment for Landlord</p>
          <div style={{ padding: '20px', textAlign: 'center', color: '#dc3545' }}>
            <p style={{ marginBottom: '10px' }}>⚠️ Stripe is not configured.</p>
            <p style={{ fontSize: '14px', color: '#666' }}>
              Please set REACT_APP_STRIPE_PUBLISHABLE_KEY in your .env file and restart the dev server.
            </p>
          </div>
        </div>
      );
    }

    if (isPlansLoading) {
      return (
        <div className="plan-select-section">
          <h2 className="payment-form-title">Choose your plan</h2>
          <p className="payment-form-subtitle">
            Pick the plan that fits your portfolio - new landlords start with a {TRIAL_DAYS}-day free trial
          </p>
          <div className="plan-card-grid">
            {[0, 1, 2].map((index) => (
              <div key={index} className="plan-card plan-card-skeleton" />
            ))}
          </div>
        </div>
      );
    }

    if (!plans.length) {
      return (
        <div className="plan-select-section">
          <h2 className="payment-form-title">Choose your plan</h2>
          <p className="plan-select-error">{initError || 'No subscription plans are available right now.'}</p>
        </div>
      );
    }

    if (step === 'select') {
      return (
        <>
          <PlanSelector
            plans={plans}
            onChoosePlan={handleChoosePlan}
            pendingPlanId={pendingPlanId}
          />
          {initError && <p className="plan-select-error">{initError}</p>}
        </>
      );
    }

    if (step === 'trial-setup') {
      return (
        <Elements stripe={stripePromise}>
          <TrialCardSetupForm
            amountText={amountText}
            intervalLabel={intervalLabel}
            planLabel={planLabel}
            trialEnd={trialEnd}
            clientSecret={clientSecret}
            onSetupSuccess={() => {
              finishSubscription();
              setSuccessMode('trial');
            }}
          />
        </Elements>
      );
    }

    return (
      <Elements stripe={stripePromise}>
        <PaymentForm
          amountText={amountText}
          intervalLabel={intervalLabel}
          planLabel={planLabel}
          clientSecret={clientSecret}
          onPaymentSuccess={() => {
            finishSubscription();
            setSuccessMode('paid');
          }}
        />
      </Elements>
    );
  };

  return (
    <div className="signup-page payment-page">
      <div className="signup-container">
        <div className="signup-info">
          <div className="signup-logo">
            <Link to="/" onClick={() => localStorage.clear()}>
              <img src={window.getPublicUrl('/assets/images/Logo.png')} alt="ClearRent" className="signup-logo-img" />
            </Link>
          </div>
          <h1 className="signup-title">Simplifying tenant Screening for landlords</h1>
          <p className="signup-subtitle">
            Create your account and get instant access to automated tenant screening.
          </p>
          <ul className="signup-features">
            <li>
              <img
                src={window.getPublicUrl('/assets/images/CircleCheckWIthBackground.svg')}
                alt="Check"
                className="signup-feature-icon"
              />
              <span>{TRIAL_DAYS}-day free trial, cancel any time</span>
            </li>
            <li>
              <img
                src={window.getPublicUrl('/assets/images/CircleCheckWIthBackground.svg')}
                alt="Check"
                className="signup-feature-icon"
              />
              <span>Unlimited property listings</span>
            </li>
            <li>
              <img
                src={window.getPublicUrl('/assets/images/CircleCheckWIthBackground.svg')}
                alt="Check"
                className="signup-feature-icon"
              />
              <span>Automated voice interviews &amp; document verification</span>
            </li>
            <li>
              <img
                src={window.getPublicUrl('/assets/images/CircleCheckWIthBackground.svg')}
                alt="Check"
                className="signup-feature-icon"
              />
              <span>Automated reference checks</span>
            </li>
          </ul>
        </div>

        <div className="signup-form-section">
          <div className={`payment-card-shell${step === 'select' ? ' payment-card-shell-wide' : ''}`}>
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletePayment;
